import { jsx } from "react/jsx-runtime";
import { useState, useEffect, useCallback } from "react";
import { VideoControllerProvider } from "./context/videoController.js";
import { VideoIsAdPlayingProvider } from "./context/state/isAdPlaying.js";
import { VideoAdBreakListProvider } from "./context/state/adBreakList.js";
import { VideoCurrentAdBreakProvider } from "./context/state/currentAdBreak.js";
import { VideoIsPausedProvider } from "./context/state/isPaused.js";
import { VideoVolumeProvider } from "./context/state/volume.js";
import { VideoTimeProvider } from "./context/state/time.js";
import { IsFullscreenProvider } from "./context/state/isFullscreen.js";
import { IsUIVisibleProvider } from "./context/state/isUIVisible.js";
import { VideoSubtitlesProvider } from "./context/state/subtitles.js";
import { VideoSubtitleTrackProvider } from "./context/state/subtitleTrack.js";
import { VideoIsReadyProvider } from "./context/state/isReady.js";
import { getGlobalContext, ReportingProvider, defaultOnError } from "./context/state/reporting.js";
import { useEvent } from "../../lib/hooks/useEvent.js";
import i18n from "i18next";
import { I18nextProvider } from "react-i18next";
import { initTranslations } from "./translations.js";
import { VideoIsBufferingProvider } from "./context/state/isBuffering.js";
function VideoContextProvider({
  children,
  onPlay,
  onPause,
  onError,
  onNonFatalError,
  videoContainerRef
}) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const handleNonFatalError = useEvent(onNonFatalError ?? defaultOnError);
  useEffect(() => {
    initTranslations();
  }, []);
  useEffect(() => {
    function fullscreenChangeListener() {
      setIsFullscreen(!!document.fullscreenElement);
    }
    const container = videoContainerRef.current;
    if (container) {
      container.addEventListener("fullscreenchange", fullscreenChangeListener);
      return () => {
        container.removeEventListener("fullscreenchange", fullscreenChangeListener);
      };
    } else {
      handleNonFatalError({
        error: new Error("Fullscreen change listener couldn't be added"),
        severity: "error",
        context: { ...getGlobalContext(), videoContainerRef: container }
      });
      return void 0;
    }
  }, [videoContainerRef, handleNonFatalError]);
  const toggleFullscreen = useCallback(async () => {
    var _a, _b, _c, _d, _e, _f;
    const isPictureInPictureSupported = typeof document !== "undefined" && "pictureInPictureEnabled" in document;
    const { pictureInPictureElement } = document;
    try {
      if (document.fullscreenElement) {
        return await document.exitFullscreen();
      }
      if (isPictureInPictureSupported && pictureInPictureElement) {
        await (document == null ? void 0 : document.exitPictureInPicture());
      }
      const requestFullscreen = ((_a = videoContainerRef == null ? void 0 : videoContainerRef.current) == null ? void 0 : _a.requestFullscreen) ? "requestFullscreen" : "webkitRequestFullscreen";
      return ((_c = (_b = videoContainerRef == null ? void 0 : videoContainerRef.current) == null ? void 0 : _b[requestFullscreen]) == null ? void 0 : _c.call(_b)) || ((_f = (_e = (_d = videoContainerRef == null ? void 0 : videoContainerRef.current) == null ? void 0 : _d.getElementsByTagName("video")) == null ? void 0 : _e[0]) == null ? void 0 : _f.webkitEnterFullscreen());
    } catch (e) {
      handleNonFatalError({
        error: e,
        severity: "error",
        context: {
          ...getGlobalContext(),
          videoContainerRef: videoContainerRef.current,
          isPictureInPictureSupported,
          pictureInPictureElement
        }
      });
    }
  }, [videoContainerRef, handleNonFatalError]);
  const togglePictureInPictureMode = useCallback(async () => {
    var _a;
    const video = (_a = videoContainerRef == null ? void 0 : videoContainerRef.current) == null ? void 0 : _a.getElementsByTagName("video")[0];
    try {
      if (document == null ? void 0 : document.pictureInPictureElement) {
        await (document == null ? void 0 : document.exitPictureInPicture());
        return;
      }
      await (video == null ? void 0 : video.requestPictureInPicture());
    } catch (e) {
      handleNonFatalError({
        error: e,
        severity: "error",
        context: {
          ...getGlobalContext(),
          videoContainerRef: videoContainerRef.current,
          video
        }
      });
    }
  }, [videoContainerRef, handleNonFatalError]);
  return /* @__PURE__ */jsx(ReportingProvider, { onError, onNonFatalError, children: /* @__PURE__ */jsx(I18nextProvider, { i18n, children: /* @__PURE__ */jsx(
        VideoControllerProvider,
        {
          toggleFullscreenHandler: toggleFullscreen,
          togglePictureInPictureModeHandler: togglePictureInPictureMode,
          children: /* @__PURE__ */jsx(VideoIsReadyProvider, { children: /* @__PURE__ */jsx(VideoIsAdPlayingProvider, { children: /* @__PURE__ */jsx(VideoIsPausedProvider, { onPlay, onPause, children: /* @__PURE__ */jsx(VideoVolumeProvider, { children: /* @__PURE__ */jsx(VideoTimeProvider, { children: /* @__PURE__ */jsx(IsFullscreenProvider, { value: isFullscreen, children: /* @__PURE__ */jsx(VideoSubtitleTrackProvider, { children: /* @__PURE__ */jsx(VideoSubtitlesProvider, { children: /* @__PURE__ */jsx(IsUIVisibleProvider, { videoContainerRef, children: /* @__PURE__ */jsx(VideoIsBufferingProvider, { children: /* @__PURE__ */jsx(VideoAdBreakListProvider, { children: /* @__PURE__ */jsx(VideoCurrentAdBreakProvider, { children }) }) }) }) }) }) }) }) }) }) }) })
        }
      ) }) });
}
export {
  VideoContextProvider };
//# sourceMappingURL=dist/components/VideoContextProvider/index.js.map